import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { api } from '../../services/api';
import type {
  Category,
  CategoryAvailability,
  FantasyExplorerFlowState,
  FantasyCatalogResponse,
  GameSettings,
  GameStatus,
  CreateGameSessionParams,
  LastAnswer,
  Mood,
  PaginatedSexPositions,
  PlayContext,
  PlayMode,
  Prompt,
  SetupAvailabilityParams,
  SpiceLevelAvailability,
  SexToy,
} from '../../types/game';
import type { SexToyAudienceQuery } from '../../gameplay/toyAudienceFromPromptType';
import {
  resolveGendersFromGameState,
  resolvePromptDirectionType,
} from '../../gameplay/resolvePromptDirectionType';
import type { PlayerGender } from '../../gameplay/coupleSetup';
import { setStoredLanguage } from '../../i18n/storage';
import { unwrapGameStatusList } from '../unwrapGameStatusList';
import { normalizeCategories } from '../../gameplay/normalizeCategory';
import { requestMoods } from '../../services/moods';
import { rateLimitFromError } from '../../services/rateLimit';
import { capExcludedPromptIds } from '../../gameplay/promptExclusions';

export type {
  Category,
  FantasyCatalogResponse,
  FantasyCatalogItem,
  FantasyCategory,
  GameSettings,
  GameStatus,
  Mood,
  PaginatedSexPositions,
  Prompt,
  SexToy,
  SexPosition,
  Tag,
} from '../../types/game';

interface Player {
  id: string;
  name: string;
  score: number;
}

interface GameActionMetadata {
  time_spent?: number;
  game_status_id?: number;
  previous_score?: number;
  [key: string]: any;
}

interface GameActionPreviousState {
  current_player?: 'player1' | 'player2';
  current_round?: number;
  player1_score?: number;
  player2_score?: number;
  status?: string;
  [key: string]: any;
}

export type GameActionType =
  | 'start_session'
  | 'complete'
  | 'skip'
  | 'revert'
  | 'pause_session'
  | 'resume_session'
  | 'complete_session'
  | 'end_session';

/** Action types the backend will not allow reverting (GameplayService::nonRevertableActionTypes). */
export const NON_REVERTABLE_ACTION_TYPES: ReadonlySet<GameActionType> = new Set(
  [
    'start_session',
    'end_session',
    'pause_session',
    'resume_session',
    'complete_session',
    'revert',
  ]
);

export interface GameSessionPlayer {
  id: number;
  game_session_id: number;
  user_id: number | null;
  player_role: 'player1' | 'player2';
  display_name: string;
  created_at?: string;
  updated_at?: string;
}

export interface GameAction {
  id: number;
  game_session_id: number;
  user_id: number;
  prompt_id?: number;
  action_type: GameActionType;
  player?: 'player1' | 'player2';
  score_change: number;
  sequence_number: number;
  previous_state?: GameActionPreviousState;
  metadata?: GameActionMetadata;
  is_reverted: boolean;
  created_at: string;
  updated_at: string;
  prompt?: Prompt;
}

export interface GameSession {
  id: number;
  user_id?: number;
  game_mode: 'solo' | 'couple';
  player1_name: string;
  player2_name: string;
  category_id?: number | null;
  spice_level?: number | null;
  /** BE-003: denormalised cursor — the last prompt played in this session. */
  last_prompt_id?: number | null;
  current_player: 'player1' | 'player2';
  current_round: number;
  player1_score: number;
  player2_score: number;
  status: string;
  /** Server-driven structured state per game leaf (e.g. Fantasy Explorer). */
  flow_state?: FantasyExplorerFlowState | Record<string, unknown> | null;
  category?: Category;
  gameActions?: GameAction[];
  session_players?: GameSessionPlayer[];
  sessionPlayers?: GameSessionPlayer[];
  /** Phase 2: the couple this session belongs to, when the owner was paired. */
  couple_id?: number | null;
  /** `two_devices`: each partner plays on their own phone. Absent means same device. */
  play_mode?: PlayMode;
  play_context?: PlayContext;
  /** Server-owned card of a `two_devices` session; cleared by complete, skip and revert. */
  current_prompt_id?: number | null;
  current_prompt_drawn_at?: string | null;
  current_prompt?: Prompt | null;
  /**
   * The server left the card out of this copy (`current_prompt` is null) or
   * blanked its text (`current_prompt.redacted`) because it is a `secret`
   * card and this reader is not the player on turn. Never set on the acting
   * seat's own REST copy.
   */
  current_prompt_redacted?: boolean;
  /**
   * The answer card chosen on the latest live turn, or null (no answer, a
   * `secret` card, or the turn was undone). Always follows the newest copy.
   */
  last_answer?: LastAnswer | null;
  created_at?: string;
  updated_at?: string;
}

/** Normalize session_players from API (snake_case or camelCase). */
export function getSessionPlayers(
  session: GameSession | null | undefined
): GameSessionPlayer[] {
  if (!session) return [];
  return session.session_players ?? session.sessionPlayers ?? [];
}

/** True when each partner plays on their own phone (Phase 2). */
export function isTwoDeviceSession(
  session: GameSession | null | undefined
): boolean {
  return session?.play_mode === 'two_devices';
}

/** The seat (player1/player2) whose account is `userId`, or null for a stranger. */
export function getSeatForUser(
  session: GameSession | null | undefined,
  userId: number | null | undefined
): 'player1' | 'player2' | null {
  if (!session || userId == null) return null;
  const seat = getSessionPlayers(session).find(p => p.user_id === userId);
  return seat?.player_role ?? null;
}

/** True when couple mode is played on one device (player2 has no remote account). */
export function isSameDeviceCoupleSession(
  session: GameSession | null | undefined
): boolean {
  if (!session || session.game_mode !== 'couple') return false;
  if (isTwoDeviceSession(session)) return false;
  const player2 = getSessionPlayers(session).find(
    p => p.player_role === 'player2'
  );
  return player2?.user_id == null;
}

/** The stable `error` code of an API failure (`{ message, error }`), if any. */
export function getApiErrorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  return typeof code === 'string' ? code : null;
}

/** Extract a user-facing message from an Axios error (message preferred for 422/429). */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = (
    error as { response?: { data?: { message?: string; error?: string } } }
  )?.response?.data;
  return data?.message || data?.error || fallback;
}

/**
 * HTTP 409 on `/complete` or `/skip`: the server already persisted this exact
 * turn (double tap or retry), so the client is behind rather than wrong.
 */
export function isReplayConflict(error: unknown): boolean {
  return (
    (error as { response?: { status?: number } } | null)?.response?.status ===
    409
  );
}

/**
 * Treats a replayed complete/skip as success: refetches the session so the
 * local turn, round and scores match the server. Resolves `null` when the
 * refetch itself fails so the caller can surface the original error.
 */
async function recoverReplayedTurn(
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  sessionId: number
): Promise<{ session: unknown; replayed: true } | null> {
  const refreshed = await dispatch(fetchGameSession(sessionId));
  if (fetchGameSession.fulfilled.match(refreshed)) {
    return { session: refreshed.payload, replayed: true };
  }
  return null;
}

/**
 * Two-device races: the partner acted first (403 `not_your_turn`), the card
 * changed under us (422 `stale_prompt`) or no card is drawn yet (422
 * `no_current_prompt`). Records the code for the page's toast and refetches so
 * the surface shows the server's card and turn; for `no_current_prompt` it
 * also draws (idempotent) so the game does not wait for a safety net.
 * Returns the code so the caller can tag the rejection with it.
 */
async function recoverTurnRace(
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  sessionId: number,
  error: unknown
): Promise<TurnErrorCode | null> {
  const code = getApiErrorCode(error);
  if (code === null || !RECOVERABLE_TURN_ERRORS.has(code)) return null;
  dispatch(setTurnError(code as TurnErrorCode));
  await dispatch(fetchGameSession(sessionId));
  if (code === 'no_current_prompt') {
    await drawAfterMissingCard(dispatch, sessionId);
  }
  return code as TurnErrorCode;
}

/**
 * Draws the card that was missing (idempotent) and applies the session. A
 * failure here is silent: the toast already explained, and the page's own
 * draw (`useTwoDeviceSession`) keeps trying without a red banner.
 */
async function drawAfterMissingCard(
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  sessionId: number
): Promise<void> {
  try {
    const response = await api.post(`/game/session/${sessionId}/draw`);
    dispatch(applyRemoteSession(response.data as GameSession));
  } catch {
    // Nothing to do: see above.
  }
}

/**
 * `rejectWithValue` with the recoverable turn error attached to the action's
 * meta (RTK merges it), so the reducer can skip the red error banner.
 */
function rejectWithTurnError(
  rejectWithValue: unknown,
  message: string,
  turnErrorCode: TurnErrorCode | null
): never {
  return (rejectWithValue as unknown as (value: string, meta: object) => never)(
    message,
    {
      turnErrorCode,
    }
  );
}

interface GameState {
  categories: Category[];
  currentPrompt: Prompt | null;
  gameSettings: GameSettings | null;
  gameHistory: GameStatus[];
  loading: boolean;
  error: string | null;
  fantasyCatalog: FantasyCatalogResponse | null;
  catalogLoading: boolean;
  sexPositions: PaginatedSexPositions | null;
  sexPositionsLoading: boolean;
  sexToysCatalogAudience: SexToyAudienceQuery | null;
  sexToysCatalogItems: SexToy[];
  sexToysCatalogLoading: boolean;
  /** Audience key for which the last `/sex-toys` request finished (cached; avoids refetch loops on empty arrays). */
  sexToysCatalogFetchCompleteFor: SexToyAudienceQuery | null;
  // New game state properties
  currentPlayer: 'player1' | 'player2';
  players: Player[];
  gameMode: 'solo' | 'couple' | null;
  playerGenders: {
    player1: PlayerGender | null;
    player2: PlayerGender | null;
  };
  currentRound: number;
  isGameActive: boolean;
  // Game session and actions
  currentSession: GameSession | null;
  gameActions: GameAction[];
  actionsLoading: boolean;
  /** Prompt IDs seen during this session, used to exclude repeats on fetchRandomPrompt. */
  seenPromptIds: number[];
  categoryAvailability: CategoryAvailability[];
  categoryAvailabilityLoading: boolean;
  spiceLevelAvailability: SpiceLevelAvailability | null;
  spiceLevelAvailabilityLoading: boolean;
  /** GET /moods (P1-BE2). A failed request leaves `moods` empty. */
  moods: Mood[];
  moodsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  /** The newest active (else paused) session, for the dashboard Resume card. */
  activeSession: GameSession | null;
  activeSessionStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  /**
   * Last recoverable turn failure (`not_your_turn`, `stale_prompt`,
   * `no_current_prompt`, `invalid_answer`).
   * The thunk has already refetched the session; the page shows a toast and clears it.
   */
  turnError: TurnErrorCode | null;
  /**
   * Bumped by every local turn mutation (complete, skip, revert). Pollers
   * compare it before and after a request and drop an answer that is older
   * than a mutation made meanwhile.
   */
  syncVersion: number;
}

export type TurnErrorCode =
  'not_your_turn' | 'stale_prompt' | 'no_current_prompt' | 'invalid_answer';

const RECOVERABLE_TURN_ERRORS: ReadonlySet<string> = new Set<TurnErrorCode>([
  'not_your_turn',
  'stale_prompt',
  'no_current_prompt',
  'invalid_answer',
]);

/** Position in the game: later turns rank higher. Reverts lower it. */
export function sessionProgress(
  session: Pick<GameSession, 'current_round' | 'current_player'>
): number {
  return (
    session.current_round * 2 + (session.current_player === 'player2' ? 1 : 0)
  );
}

function timestampOf(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * True when `incoming` is an out-of-date copy of `current` (a late socket
 * event or poll answer). A completed session never goes back.
 */
export function isStaleSession(
  current: GameSession,
  incoming: GameSession
): boolean {
  if (current.status === 'completed' && incoming.status !== 'completed') {
    return true;
  }
  const currentAt = timestampOf(current.updated_at);
  const incomingAt = timestampOf(incoming.updated_at);
  if (currentAt != null && incomingAt != null) {
    if (incomingAt < currentAt) return true;
    if (incomingAt === currentAt) {
      return sessionProgress(incoming) < sessionProgress(current);
    }
  }
  return false;
}

/** The copy carries a secret card that was left out or blanked for this reader. */
function isRedactedCopy(session: GameSession): boolean {
  return (
    session.current_prompt_redacted === true ||
    session.current_prompt?.redacted === true
  );
}

/**
 * Merges a fresh copy of a session over the one in the store. Fields a
 * response may leave out (players, category, the drawn card) are kept.
 */
export function mergeSession(
  previous: GameSession | null | undefined,
  incoming: GameSession
): GameSession {
  const merged: GameSession = { ...previous, ...incoming };
  const players = getSessionPlayers(incoming);
  if (players.length === 0 && previous) {
    const kept = getSessionPlayers(previous);
    if (kept.length > 0) merged.session_players = kept;
  }
  if (incoming.category === undefined && previous?.category) {
    merged.category = previous.category;
  }
  if (incoming.current_prompt === undefined) {
    merged.current_prompt =
      previous?.current_prompt &&
      previous.current_prompt.id === incoming.current_prompt_id
        ? previous.current_prompt
        : null;
  }
  // A redacted copy (a broadcast leaves a secret card out, the partner's REST
  // copy has its text blanked) must never overwrite the full card this phone
  // already holds for the same id: that is the acting seat's own card.
  if (
    isRedactedCopy(incoming) &&
    previous?.current_prompt &&
    previous.current_prompt.redacted !== true &&
    previous.current_prompt.id === incoming.current_prompt_id
  ) {
    merged.current_prompt = previous.current_prompt;
    delete merged.current_prompt_redacted;
  }
  // A card belongs to one turn: a payload that moves the game on without
  // saying which card is current (a partial resource) cannot keep the old one.
  if (
    previous &&
    isTwoDeviceSession(merged) &&
    incoming.current_prompt_id === undefined &&
    sessionProgress(incoming) !== sessionProgress(previous)
  ) {
    merged.current_prompt_id = null;
    merged.current_prompt = null;
  }
  // `last_answer` belongs to the newest copy, null included. A copy that
  // leaves the key out but moves the game on (a partial resource) cannot keep
  // the answer of the turn before.
  if (
    previous &&
    incoming.last_answer === undefined &&
    sessionProgress(incoming) !== sessionProgress(previous)
  ) {
    merged.last_answer = null;
  }
  return merged;
}

/** Copies the session's turn state into the flat fields the UI reads. */
function syncLocalFromSession(state: GameState, session: GameSession): void {
  state.currentPlayer = session.current_player;
  state.currentRound = session.current_round;
  state.players[0].score = session.player1_score;
  state.players[1].score = session.player2_score;
  state.gameMode = session.game_mode;
  state.players[0].name = session.player1_name;
  state.players[1].name = session.player2_name;
}

/**
 * Idempotent merge of a session that arrived from a socket event, a poll or
 * `draw`. Returns false when it was ignored as stale.
 */
function applyRemoteSessionToState(
  state: GameState,
  incoming: GameSession | null | undefined
): boolean {
  if (!incoming || typeof incoming.id !== 'number') return false;
  const current = state.currentSession;
  if (current && current.id !== incoming.id) return false;
  if (current && isStaleSession(current, incoming)) return false;

  const merged = mergeSession(current, incoming);
  state.currentSession = merged;
  syncLocalFromSession(state, merged);
  if (isTwoDeviceSession(merged)) {
    if (merged.current_prompt) {
      if (
        state.currentPrompt?.id !== merged.current_prompt.id ||
        replacesRedactedCard(state.currentPrompt, merged.current_prompt)
      ) {
        state.currentPrompt = merged.current_prompt;
      }
    } else if (
      merged.current_prompt_id != null &&
      state.currentPrompt &&
      state.currentPrompt.id !== merged.current_prompt_id
    ) {
      // Another card is current but its text did not come with this copy:
      // never keep showing the old one; the next draw (idempotent) fetches it.
      state.currentPrompt = null;
    }
  }
  return true;
}

/** The card on screen is a blanked copy and the incoming one (same id) is the full card. */
function replacesRedactedCard(
  held: Prompt | null,
  incoming: Prompt | null | undefined
): boolean {
  return (
    held != null &&
    incoming != null &&
    held.id === incoming.id &&
    held.redacted === true &&
    incoming.redacted !== true
  );
}

/** Normalize session payload from API (snake_case or camelCase) into live game state. */
function applyChallengeResponseToGameState(
  state: GameState,
  payload: Record<string, unknown>
): void {
  const session = payload.session as Record<string, unknown> | undefined;
  if (session && typeof session === 'object') {
    const typed = session as unknown as GameSession;
    state.currentSession = isTwoDeviceSession(typed)
      ? mergeSession(state.currentSession, typed)
      : typed;
  }

  const turn =
    (session?.current_player as string | undefined) ??
    (session?.currentPlayer as string | undefined) ??
    (payload.next_player as string | undefined) ??
    (payload.nextPlayer as string | undefined);
  if (turn === 'player1' || turn === 'player2') {
    state.currentPlayer = turn;
  }

  const round =
    typeof session?.current_round === 'number'
      ? session.current_round
      : typeof session?.currentRound === 'number'
        ? (session.currentRound as number)
        : undefined;
  if (round !== undefined) {
    state.currentRound = round;
  }

  const p1 =
    typeof session?.player1_score === 'number'
      ? session.player1_score
      : typeof session?.player1Score === 'number'
        ? (session.player1Score as number)
        : undefined;
  const p2 =
    typeof session?.player2_score === 'number'
      ? session.player2_score
      : typeof session?.player2Score === 'number'
        ? (session.player2Score as number)
        : undefined;
  if (p1 !== undefined) {
    state.players[0].score = p1;
  }
  if (p2 !== undefined) {
    state.players[1].score = p2;
  }

  const actions = session?.game_actions ?? session?.gameActions;
  if (Array.isArray(actions)) {
    state.gameActions = actions as GameAction[];
  }

  if (payload.prompt) {
    state.currentPrompt = payload.prompt as Prompt;
  }
}

const initialState: GameState = {
  categories: [],
  currentPrompt: null,
  gameSettings: null,
  gameHistory: [],
  loading: false,
  error: null,
  fantasyCatalog: null,
  catalogLoading: false,
  sexPositions: null,
  sexPositionsLoading: false,
  sexToysCatalogAudience: null,
  sexToysCatalogItems: [],
  sexToysCatalogLoading: false,
  sexToysCatalogFetchCompleteFor: null,
  // Initialize new game state
  currentPlayer: 'player1',
  players: [
    { id: 'player1', name: 'Player 1', score: 0 },
    { id: 'player2', name: 'Player 2', score: 0 },
  ],
  gameMode: null,
  playerGenders: { player1: null, player2: null },
  currentRound: 1,
  isGameActive: false,
  // Game session and actions
  currentSession: null,
  gameActions: [],
  actionsLoading: false,
  seenPromptIds: [],
  categoryAvailability: [],
  categoryAvailabilityLoading: false,
  spiceLevelAvailability: null,
  spiceLevelAvailabilityLoading: false,
  moods: [],
  moodsStatus: 'idle',
  activeSession: null,
  activeSessionStatus: 'idle',
  turnError: null,
  syncVersion: 0,
};

/** Laravel paginator (`{ data: [...] }`) or a plain array. */
function unwrapSessionList(payload: unknown): GameSession[] {
  const list = Array.isArray(payload)
    ? payload
    : payload &&
        typeof payload === 'object' &&
        Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : [];
  return list.filter(
    (item): item is GameSession =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as GameSession).id === 'number'
  );
}

/** Sessions that can be resumed: a completed one never is, whatever the API says. */
export function isResumableSession(session: GameSession | null): boolean {
  return session != null && ['active', 'paused'].includes(session.status);
}

export const fetchMoods = createAsyncThunk(
  'game/fetchMoods',
  async (_, { rejectWithValue }) => {
    try {
      return await requestMoods();
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load moods'));
    }
  }
);

/**
 * GET /game/sessions?status=active&per_page=1, falling back to the newest
 * paused session. Resolves null when there is nothing to resume.
 */
export const fetchActiveSession = createAsyncThunk(
  'game/fetchActiveSession',
  async (_, { rejectWithValue }) => {
    try {
      for (const status of ['active', 'paused'] as const) {
        const response = await api.get('/game/sessions', {
          params: { status, per_page: 1 },
        });
        const found = unwrapSessionList(response.data).find(
          session => session.status === status
        );
        if (found) return found;
      }
      return null;
    } catch (error: any) {
      const message = getApiErrorMessage(
        error,
        'Failed to load the current game'
      );
      const limited = rateLimitFromError(error);
      if (limited) {
        // Lets the dashboard poll back off (thunks never throw to the poll loop).
        return (
          rejectWithValue as unknown as (value: string, meta: object) => never
        )(message, { rateLimit: { retryAfterMs: limited.retryAfterMs } });
      }
      return rejectWithValue(message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'game/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/categories');
      return normalizeCategories(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch categories'
      );
    }
  }
);

export const fetchSubcategories = createAsyncThunk(
  'game/fetchSubcategories',
  async (categoryId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/subcategories/${categoryId}`);
      return { categoryId, subcategories: normalizeCategories(response.data) };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch subcategories'
      );
    }
  }
);

export type FetchSpiceLevelsParams = SetupAvailabilityParams & {
  category_id: number;
};

export const fetchCategoryAvailability = createAsyncThunk(
  'game/fetchCategoryAvailability',
  async (params: SetupAvailabilityParams, { rejectWithValue }) => {
    try {
      const response = await api.get<{ categories: CategoryAvailability[] }>(
        '/game/setup/category-availability',
        { params }
      );
      return response.data.categories;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch category availability')
      );
    }
  }
);

export const fetchSpiceLevelsForCategory = createAsyncThunk(
  'game/fetchSpiceLevelsForCategory',
  async (params: FetchSpiceLevelsParams, { rejectWithValue }) => {
    try {
      const response = await api.get<SpiceLevelAvailability>(
        '/game/setup/spice-levels',
        { params }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch spice levels for category')
      );
    }
  }
);

export type FetchPromptsParams = {
  category_id?: number;
  spice_level?: number;
  type?: string;
  explicit?: boolean;
  page?: number;
  per_page?: number;
  /** Capped to the newest 200 before it is sent. */
  exclude_prompt_ids?: number[];
};

export const fetchFantasyCatalog = createAsyncThunk(
  'game/fetchFantasyCatalog',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await api.get<FantasyCatalogResponse>('/fantasy-catalog');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'fantasy_catalog_failed'
      );
    }
  }
);

export const fetchSexToysCatalog = createAsyncThunk(
  'game/fetchSexToysCatalog',
  async (audience: SexToyAudienceQuery, { rejectWithValue, getState }) => {
    const game = (getState() as { game: GameState }).game;
    if (
      game.sexToysCatalogFetchCompleteFor === audience &&
      game.sexToysCatalogAudience === audience
    ) {
      return {
        audience,
        items: game.sexToysCatalogItems,
      };
    }

    try {
      const response = await api.get<SexToy[]>('/sex-toys', {
        params: { audience },
      });
      const payload = response.data;
      return {
        audience,
        items: Array.isArray(payload) ? payload : [],
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load toy catalog'
      );
    }
  }
);

/** The API caps `per_page` at 50 on `GET /sex-positions`. */
export const SEX_POSITIONS_PAGE_SIZE = 50;
/** Never walk more pages than this (6 x 50 = 300 positions). */
export const SEX_POSITIONS_MAX_PAGES = 6;

/**
 * Loads the position catalogue page by page (50 per request) following
 * `meta.last_page`, at most `SEX_POSITIONS_MAX_PAGES` pages, and resolves one
 * payload with every row in `data`. A failure on a later page keeps what was
 * loaded; a failure on the first page rejects.
 */
export const fetchSexPositions = createAsyncThunk(
  'game/fetchSexPositions',
  async (
    arg: { per_page?: number; page?: number } | undefined,
    { rejectWithValue }
  ) => {
    const pageSize = Math.min(
      Math.max(arg?.per_page ?? SEX_POSITIONS_PAGE_SIZE, 1),
      SEX_POSITIONS_PAGE_SIZE
    );
    const firstPage = Math.max(arg?.page ?? 1, 1);
    try {
      const first = await api.get<PaginatedSexPositions>('/sex-positions', {
        params: { per_page: pageSize, page: firstPage },
      });
      const merged: PaginatedSexPositions = {
        ...first.data,
        data: [...(first.data.data ?? [])],
      };
      const lastPage = first.data.meta?.last_page ?? firstPage;
      const stopAt = Math.min(
        lastPage,
        firstPage + SEX_POSITIONS_MAX_PAGES - 1
      );
      for (let page = firstPage + 1; page <= stopAt; page += 1) {
        try {
          const next = await api.get<PaginatedSexPositions>('/sex-positions', {
            params: { per_page: pageSize, page },
          });
          merged.data.push(...(next.data.data ?? []));
          if (merged.meta && next.data.meta) {
            merged.meta = { ...next.data.meta, current_page: page };
          }
        } catch {
          break;
        }
      }
      return merged;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch sex positions'
      );
    }
  }
);

export const fetchPrompts = createAsyncThunk(
  'game/fetchPrompts',
  async (
    params: FetchPromptsParams = {},
    { rejectWithValue, getState }: any = {
      rejectWithValue: () => {},
      getState: () => {},
    }
  ) => {
    try {
      // Get user settings from Redux state
      const state = getState() as { game: GameState };
      const gameSettings = state.game.gameSettings;

      // Build params object with user settings
      const apiParams: any = { ...params };
      if (params.exclude_prompt_ids) {
        apiParams.exclude_prompt_ids = capExcludedPromptIds(
          params.exclude_prompt_ids
        );
      }

      if (gameSettings) {
        // Include user settings in API call
        if (gameSettings.difficulty_level) {
          apiParams.difficulty_level = gameSettings.difficulty_level;
        }
        if (gameSettings.show_explicit !== undefined) {
          apiParams.show_explicit = gameSettings.show_explicit;
        }
        if (gameSettings.language) {
          apiParams.language = gameSettings.language;
        }
      }

      const response = await api.get('/prompts', { params: apiParams });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch prompts'
      );
    }
  }
);

export type FetchRandomPromptParams = {
  category_id?: number;
  spice_level?: number;
  type?: string;
  /** Capped to the newest 200 (the API limit) before it is sent. */
  exclude_prompt_ids?: number[];
  /** BE-001: bind this draw to an active session (validates ownership, inherits session filters). */
  game_session_id?: number;
};

export const fetchRandomPrompt = createAsyncThunk(
  'game/fetchRandomPrompt',
  async (
    params?: FetchRandomPromptParams,
    { rejectWithValue, getState }: any = {
      rejectWithValue: () => {},
      getState: () => {},
    }
  ) => {
    try {
      // Get user settings from Redux state
      const state = getState() as { game: GameState };
      const gameSettings = state.game.gameSettings;

      // Build params object with user settings
      const apiParams: Record<string, unknown> = { ...(params || {}) };
      if (params?.exclude_prompt_ids) {
        // The API refuses more than 200 ids: send the newest ones.
        apiParams.exclude_prompt_ids = capExcludedPromptIds(
          params.exclude_prompt_ids
        );
      }

      if (gameSettings) {
        const sessionSpice = state.game.currentSession?.spice_level;
        const explicitSpice = params?.spice_level ?? sessionSpice ?? undefined;

        if (!explicitSpice && gameSettings.difficulty_level) {
          apiParams.difficulty_level = gameSettings.difficulty_level;
        }
        if (gameSettings.show_explicit !== undefined) {
          apiParams.show_explicit = gameSettings.show_explicit;
        }
        if (gameSettings.language) {
          apiParams.language = gameSettings.language;
        }
      }

      if (!apiParams.type) {
        const genders = resolveGendersFromGameState(state.game);
        const directionType = resolvePromptDirectionType(
          state.game.gameMode ?? state.game.currentSession?.game_mode ?? null,
          state.game.currentPlayer,
          genders
        );
        if (directionType) {
          apiParams.type = directionType;
        }
      }

      const response = await api.get('/game/random-prompt', {
        params: apiParams,
        // Laravel expects repeated keys or `key[]` for array query params; default axios
        // serializes arrays as `exclude_prompt_ids=1` (scalar) which fails validation.
        paramsSerializer: (params: Record<string, unknown>) => {
          const parts: string[] = [];
          for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) {
              continue;
            }
            if (Array.isArray(value)) {
              for (const item of value) {
                if (item === undefined || item === null) {
                  continue;
                }
                parts.push(
                  `${encodeURIComponent(key)}[]=${encodeURIComponent(String(item))}`
                );
              }
            } else {
              parts.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
              );
            }
          }
          return parts.join('&');
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch random prompt')
      );
    }
  }
);

export const fetchGameSettings = createAsyncThunk(
  'game/fetchGameSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/game/settings');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch game settings'
      );
    }
  }
);

export const updateGameSettings = createAsyncThunk(
  'game/updateGameSettings',
  async (
    settings: Partial<GameSettings> & Record<string, unknown>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/game/settings', settings);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update game settings'
      );
    }
  }
);

export const fetchGameStatus = createAsyncThunk(
  'game/fetchGameStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/game/status', {
        params: { per_page: 100, sort_by: 'created_at', sort_order: 'desc' },
      });
      return unwrapGameStatusList(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch game status'
      );
    }
  }
);

export const updateGameStatus = createAsyncThunk(
  'game/updateGameStatus',
  async (status: Partial<GameStatus>, { rejectWithValue }) => {
    try {
      const response = await api.post('/game/status', status);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update game status'
      );
    }
  }
);

// Game Session API thunks
export const fetchGameSession = createAsyncThunk(
  'game/fetchGameSession',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/game/session/${sessionId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch game session')
      );
    }
  }
);

export const fetchGameActions = createAsyncThunk(
  'game/fetchGameActions',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/game/session/${sessionId}/actions`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch game actions')
      );
    }
  }
);

export const revertLastAction = createAsyncThunk(
  'game/revertLastAction',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/game/session/${sessionId}/revert`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return rejectWithValue('No action to revert');
      }
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to revert action')
      );
    }
  }
);

/** PATCH /game/session/:id/status — mark a session completed, paused, or active. */
export const updateSessionStatus = createAsyncThunk(
  'game/updateSessionStatus',
  async (
    {
      sessionId,
      status,
    }: { sessionId: number; status: 'active' | 'completed' | 'paused' },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(`/game/session/${sessionId}/status`, {
        status,
      });
      return response.data as { session: GameSession };
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to update session status')
      );
    }
  }
);

export const completeChallenge = createAsyncThunk(
  'game/completeChallenge',
  async (
    {
      sessionId,
      promptId,
      score,
      timeSpent,
      metadata,
      answerKey,
    }: {
      sessionId: number;
      promptId: number;
      score: number;
      timeSpent: number;
      /** BE-002: optional mechanic-specific payload (e.g. bottle target, toy id). */
      metadata?: Record<string, unknown>;
      /** Question cards: the chosen answer card's key (422 `invalid_answer` if unknown). */
      answerKey?: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/game/session/${sessionId}/complete`, {
        prompt_id: promptId,
        score,
        time_spent: timeSpent,
        ...(answerKey ? { answer_key: answerKey } : {}),
        ...(metadata ? { metadata } : {}),
      });
      return response.data;
    } catch (error: any) {
      if (isReplayConflict(error)) {
        const recovered = await recoverReplayedTurn(dispatch, sessionId);
        if (recovered) {
          return recovered;
        }
      }
      const turnErrorCode = await recoverTurnRace(dispatch, sessionId, error);
      return rejectWithTurnError(
        rejectWithValue,
        getApiErrorMessage(error, 'Failed to complete challenge'),
        turnErrorCode
      );
    }
  }
);

/** POST /game/session/:id/skip (async thunk; local turn skip is `skipChallengeTurn`) */
export const submitSkipChallenge = createAsyncThunk(
  'game/skipChallenge',
  async (
    {
      sessionId,
      promptId,
      metadata,
    }: {
      sessionId: number;
      promptId: number;
      /** BE-002: optional mechanic-specific payload. */
      metadata?: Record<string, unknown>;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/game/session/${sessionId}/skip`, {
        prompt_id: promptId,
        ...(metadata ? { metadata } : {}),
      });
      return response.data;
    } catch (error: any) {
      if (isReplayConflict(error)) {
        const recovered = await recoverReplayedTurn(dispatch, sessionId);
        if (recovered) {
          return recovered;
        }
      }
      const turnErrorCode = await recoverTurnRace(dispatch, sessionId, error);
      return rejectWithTurnError(
        rejectWithValue,
        getApiErrorMessage(error, 'Failed to skip challenge'),
        turnErrorCode
      );
    }
  }
);

export const updateGameFlowState = createAsyncThunk(
  'game/updateGameFlowState',
  async (
    {
      sessionId,
      flowState,
    }: {
      sessionId: number;
      flowState: FantasyExplorerFlowState | Record<string, unknown>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        `/game/session/${sessionId}/flow-state`,
        {
          flow_state: flowState,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to save flow'
      );
    }
  }
);

export const fetchFantasyExplorerMatch = createAsyncThunk(
  'game/fetchFantasyExplorerMatch',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/game/session/${sessionId}/fantasy-explorer/match`
      );
      return response.data.match;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load match preview'
      );
    }
  }
);

export const joinGameSession = createAsyncThunk(
  'game/joinGameSession',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/game/session/${sessionId}/join`);
      return response.data as GameSession;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to join game session')
      );
    }
  }
);

/**
 * POST /game/session/:id/draw: the server picks the card of a `two_devices`
 * session and both phones show it. Idempotent while the card is unplayed, so
 * either device (or both at once) may call it. Resolves the session.
 */
export const drawPrompt = createAsyncThunk(
  'game/drawPrompt',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/game/session/${sessionId}/draw`);
      return response.data as GameSession;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to draw a card')
      );
    }
  }
);

export const createGameSession = createAsyncThunk(
  'game/createGameSession',
  async (
    params: CreateGameSessionParams,
    { rejectWithValue, getState }: any = {
      rejectWithValue: () => {},
      getState: () => {},
    }
  ) => {
    try {
      // Get user settings from Redux state
      const state = getState() as { game: GameState };
      const gameSettings = state.game.gameSettings;

      // Build params object with user settings
      const apiParams: any = { ...params };

      if (gameSettings) {
        if (gameSettings.difficulty_level) {
          apiParams.difficulty_level = gameSettings.difficulty_level;
        }
        if (gameSettings.show_explicit !== undefined) {
          apiParams.show_explicit = gameSettings.show_explicit;
        }
        if (gameSettings.language) {
          apiParams.language = gameSettings.language;
        }
      }

      const response = await api.post('/game/session', apiParams);
      return response.data;
    } catch (error: any) {
      const sessionId = error.response?.data?.session_id;
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to create game session',
        typeof sessionId === 'number' ? { sessionId } : undefined
      );
    }
  }
);

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setCurrentPrompt: (state, action: PayloadAction<Prompt>) => {
      state.currentPrompt = action.payload;
    },
    clearCurrentPrompt: state => {
      state.currentPrompt = null;
    },
    /**
     * Applies settings locally and immediately (play-loop toggles). The caller
     * persists with `updateGameSettings` and rolls back on failure.
     */
    applyGameSettingsLocal: (
      state,
      action: PayloadAction<Partial<GameSettings>>
    ) => {
      if (state.gameSettings) {
        state.gameSettings = { ...state.gameSettings, ...action.payload };
      }
    },
    // New reducers for game flow
    initializeGame: (
      state,
      action: PayloadAction<{
        gameMode: 'solo' | 'couple';
        player1Name: string;
        player2Name: string;
        player1Gender?: PlayerGender;
        player2Gender?: PlayerGender;
      }>
    ) => {
      state.gameMode = action.payload.gameMode;
      state.players[0].name = action.payload.player1Name;
      state.players[1].name = action.payload.player2Name;
      state.playerGenders = {
        player1: action.payload.player1Gender ?? null,
        player2: action.payload.player2Gender ?? null,
      };
      state.currentPlayer = 'player1';
      state.currentRound = 1;
      state.isGameActive = true;
      state.players[0].score = 0;
      state.players[1].score = 0;
    },
    switchPlayer: state => {
      state.currentPlayer =
        state.currentPlayer === 'player1' ? 'player2' : 'player1';
      if (state.currentPlayer === 'player1') {
        state.currentRound++;
      }
    },
    updatePlayerScore: (
      state,
      action: PayloadAction<{ playerId: string; score: number }>
    ) => {
      const player = state.players.find(p => p.id === action.payload.playerId);
      if (player) {
        player.score += action.payload.score;
      }
    },
    skipChallengeTurn: state => {
      // Player gets 0 points for skipping
      const player = state.players.find(p => p.id === state.currentPlayer);
      if (player) {
        player.score += 0; // Explicitly 0 points
      }
      // Switch to next player
      state.currentPlayer =
        state.currentPlayer === 'player1' ? 'player2' : 'player1';
      if (state.currentPlayer === 'player1') {
        state.currentRound++;
      }
    },
    resetGame: state => {
      state.currentPlayer = 'player1';
      state.currentRound = 1;
      state.isGameActive = false;
      state.gameMode = null;
      state.playerGenders = { player1: null, player2: null };
      state.players[0].score = 0;
      state.players[1].score = 0;
      state.currentPrompt = null;
      state.currentSession = null;
      state.gameActions = [];
      state.seenPromptIds = [];
      state.sexToysCatalogAudience = null;
      state.sexToysCatalogItems = [];
      state.sexToysCatalogLoading = false;
      state.sexToysCatalogFetchCompleteFor = null;
      state.turnError = null;
    },
    /**
     * A session arrived from a socket event or a poll. Idempotent: the same
     * copy twice changes nothing, and an older copy never replaces a newer one.
     */
    applyRemoteSession: (state, action: PayloadAction<GameSession>) => {
      applyRemoteSessionToState(state, action.payload);
    },
    /** `{ session, action }` events (turn completed, skipped, reverted). */
    applyRemoteAction: (
      state,
      action: PayloadAction<{ session: GameSession; action?: GameAction }>
    ) => {
      applyRemoteSessionToState(state, action.payload.session);
      const incoming = action.payload.action;
      if (incoming && typeof incoming.id === 'number') {
        const index = state.gameActions.findIndex(a => a.id === incoming.id);
        if (index >= 0) {
          state.gameActions[index] = incoming;
        } else if (incoming.game_session_id === state.currentSession?.id) {
          state.gameActions.push(incoming);
          state.gameActions.sort(
            (a, b) => a.sequence_number - b.sequence_number
          );
        }
      }
    },
    setTurnError: (state, action: PayloadAction<TurnErrorCode>) => {
      state.turnError = action.payload;
    },
    clearTurnError: state => {
      state.turnError = null;
    },
    setCurrentSession: (state, action: PayloadAction<GameSession>) => {
      state.currentSession = action.payload;
      // Sync local state with session state
      if (action.payload) {
        state.currentPlayer = action.payload.current_player;
        state.currentRound = action.payload.current_round;
        state.players[0].score = action.payload.player1_score;
        state.players[1].score = action.payload.player2_score;
        state.gameMode = action.payload.game_mode;
        state.players[0].name = action.payload.player1_name;
        state.players[1].name = action.payload.player2_name;
        const coupleSetup = (
          action.payload.flow_state as {
            couple_setup?: {
              player1_gender?: PlayerGender;
              player2_gender?: PlayerGender;
            };
          } | null
        )?.couple_setup;
        if (coupleSetup) {
          state.playerGenders = {
            player1: coupleSetup.player1_gender ?? null,
            player2: coupleSetup.player2_gender ?? null,
          };
        }
        if (action.payload.gameActions) {
          state.gameActions = action.payload.gameActions;
        }
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Categories
      .addCase(fetchCategories.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCategoryAvailability.pending, state => {
        state.categoryAvailabilityLoading = true;
      })
      .addCase(fetchCategoryAvailability.fulfilled, (state, action) => {
        state.categoryAvailabilityLoading = false;
        state.categoryAvailability = action.payload;
      })
      .addCase(fetchCategoryAvailability.rejected, state => {
        state.categoryAvailabilityLoading = false;
        state.categoryAvailability = [];
      })
      .addCase(fetchSpiceLevelsForCategory.pending, state => {
        state.spiceLevelAvailabilityLoading = true;
        state.spiceLevelAvailability = null;
      })
      .addCase(fetchSpiceLevelsForCategory.fulfilled, (state, action) => {
        state.spiceLevelAvailabilityLoading = false;
        state.spiceLevelAvailability = action.payload;
      })
      .addCase(fetchSpiceLevelsForCategory.rejected, state => {
        state.spiceLevelAvailabilityLoading = false;
        state.spiceLevelAvailability = null;
      })
      .addCase(fetchFantasyCatalog.pending, state => {
        state.catalogLoading = true;
        state.error = null;
      })
      .addCase(fetchFantasyCatalog.fulfilled, (state, action) => {
        state.catalogLoading = false;
        state.fantasyCatalog = action.payload;
      })
      .addCase(fetchFantasyCatalog.rejected, (state, action) => {
        state.catalogLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSexPositions.pending, state => {
        state.sexPositionsLoading = true;
      })
      .addCase(fetchSexPositions.fulfilled, (state, action) => {
        state.sexPositionsLoading = false;
        state.sexPositions = action.payload;
      })
      .addCase(fetchSexPositions.rejected, (state, action) => {
        state.sexPositionsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSexToysCatalog.pending, state => {
        state.sexToysCatalogLoading = true;
      })
      .addCase(fetchSexToysCatalog.fulfilled, (state, action) => {
        state.sexToysCatalogLoading = false;
        state.sexToysCatalogAudience = action.payload.audience;
        state.sexToysCatalogItems = action.payload.items;
        state.sexToysCatalogFetchCompleteFor = action.payload.audience;
      })
      .addCase(fetchSexToysCatalog.rejected, (state, action) => {
        state.sexToysCatalogLoading = false;
        state.error = action.payload as string;
        const audience = action.meta.arg;
        state.sexToysCatalogFetchCompleteFor = audience;
        state.sexToysCatalogAudience = audience;
        state.sexToysCatalogItems = [];
      })
      // Fetch Random Prompt
      .addCase(fetchRandomPrompt.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRandomPrompt.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPrompt = action.payload;
        if (
          action.payload?.id &&
          !state.seenPromptIds.includes(action.payload.id)
        ) {
          state.seenPromptIds.push(action.payload.id);
        }
      })
      .addCase(fetchRandomPrompt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Game Settings
      .addCase(fetchGameSettings.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGameSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.gameSettings = action.payload;
        if (action.payload?.language) {
          setStoredLanguage(action.payload.language);
        }
      })
      .addCase(fetchGameSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Game Settings
      .addCase(updateGameSettings.fulfilled, (state, action) => {
        state.gameSettings = action.payload;
        if (action.payload?.language) {
          setStoredLanguage(action.payload.language);
        }
      })
      // Fetch Game Status
      .addCase(fetchGameStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGameStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.gameHistory = action.payload;
      })
      .addCase(fetchGameStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Game Status
      .addCase(updateGameStatus.fulfilled, (state, action) => {
        const index = state.gameHistory.findIndex(
          status => status.id === action.payload.id
        );
        if (index !== -1) {
          state.gameHistory[index] = action.payload;
        } else {
          state.gameHistory.push(action.payload);
        }
      })
      // Create Game Session
      .addCase(createGameSession.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGameFlowState.fulfilled, (state, action) => {
        // Merged, not replaced: the answer may leave out the seats and the card.
        state.currentSession = mergeSession(
          state.currentSession,
          action.payload as GameSession
        );
      })
      .addCase(createGameSession.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload;
        state.isGameActive = true;
        // Sync local state
        if (action.payload) {
          state.currentPlayer = action.payload.current_player;
          state.currentRound = action.payload.current_round;
          state.players[0].score = action.payload.player1_score;
          state.players[1].score = action.payload.player2_score;
          state.gameMode = action.payload.game_mode;
          state.players[0].name = action.payload.player1_name;
          state.players[1].name = action.payload.player2_name;
          if (action.payload.gameActions) {
            state.gameActions = action.payload.gameActions;
          }
        }
      })
      .addCase(createGameSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Game Session
      .addCase(fetchGameSession.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGameSession.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload;
        // Sync local state
        if (action.payload) {
          state.currentPlayer = action.payload.current_player;
          state.currentRound = action.payload.current_round;
          state.players[0].score = action.payload.player1_score;
          state.players[1].score = action.payload.player2_score;
          state.gameMode = action.payload.game_mode;
          state.players[0].name = action.payload.player1_name;
          state.players[1].name = action.payload.player2_name;
          if (action.payload.gameActions) {
            state.gameActions = action.payload.gameActions;
          }
          // The server owns the card of a two-device session: a refetch is
          // authoritative, including "no card right now".
          if (isTwoDeviceSession(action.payload)) {
            state.currentPrompt = action.payload.current_prompt ?? null;
          }
        }
      })
      .addCase(fetchGameSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(joinGameSession.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(joinGameSession.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload;
        if (action.payload) {
          state.currentPlayer = action.payload.current_player;
          state.currentRound = action.payload.current_round;
          state.players[0].score = action.payload.player1_score;
          state.players[1].score = action.payload.player2_score;
          state.gameMode = action.payload.game_mode;
          state.players[0].name = action.payload.player1_name;
          state.players[1].name = action.payload.player2_name;
        }
      })
      .addCase(joinGameSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Draw the shared card (two devices)
      .addCase(drawPrompt.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(drawPrompt.fulfilled, (state, action) => {
        state.loading = false;
        applyRemoteSessionToState(state, action.payload);
        const drawn = action.payload?.current_prompt;
        if (
          drawn?.id &&
          (state.currentPrompt?.id !== drawn.id ||
            replacesRedactedCard(state.currentPrompt, drawn))
        ) {
          state.currentPrompt = drawn;
        }
      })
      .addCase(drawPrompt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Game Actions
      .addCase(fetchGameActions.pending, state => {
        state.actionsLoading = true;
      })
      .addCase(fetchGameActions.fulfilled, (state, action) => {
        state.actionsLoading = false;
        state.gameActions = action.payload;
      })
      .addCase(fetchGameActions.rejected, state => {
        state.actionsLoading = false;
      })
      // Revert Last Action
      .addCase(revertLastAction.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(revertLastAction.fulfilled, (state, action) => {
        state.loading = false;
        state.syncVersion += 1;
        applyChallengeResponseToGameState(
          state,
          action.payload as Record<string, unknown>
        );
      })
      .addCase(revertLastAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update session status (end-game / pause)
      .addCase(updateSessionStatus.fulfilled, (state, action) => {
        if (action.payload?.session) {
          state.currentSession = action.payload.session;
          if (
            state.activeSession?.id === action.payload.session.id &&
            !isResumableSession(action.payload.session)
          ) {
            state.activeSession = null;
          }
        }
      })
      .addCase(fetchMoods.pending, state => {
        state.moodsStatus = 'loading';
      })
      .addCase(fetchMoods.fulfilled, (state, action) => {
        state.moodsStatus = 'succeeded';
        state.moods = action.payload;
      })
      .addCase(fetchMoods.rejected, state => {
        state.moodsStatus = 'failed';
        state.moods = [];
      })
      .addCase(fetchActiveSession.pending, state => {
        state.activeSessionStatus = 'loading';
      })
      .addCase(fetchActiveSession.fulfilled, (state, action) => {
        state.activeSessionStatus = 'succeeded';
        state.activeSession = action.payload;
      })
      .addCase(fetchActiveSession.rejected, state => {
        state.activeSessionStatus = 'failed';
        state.activeSession = null;
      })
      // Complete Challenge
      .addCase(completeChallenge.pending, state => {
        state.loading = true;
        state.error = null;
        state.turnError = null;
      })
      .addCase(completeChallenge.fulfilled, (state, action) => {
        state.loading = false;
        state.syncVersion += 1;
        applyChallengeResponseToGameState(
          state,
          action.payload as Record<string, unknown>
        );
      })
      .addCase(completeChallenge.rejected, (state, action) => {
        state.loading = false;
        // A recoverable two-device race is shown as a toast, not an error banner.
        if (!(action.meta as { turnErrorCode?: string | null }).turnErrorCode) {
          state.error = action.payload as string;
        }
      })
      // Skip Challenge (API)
      .addCase(submitSkipChallenge.pending, state => {
        state.loading = true;
        state.error = null;
        state.turnError = null;
      })
      .addCase(submitSkipChallenge.fulfilled, (state, action) => {
        state.loading = false;
        state.syncVersion += 1;
        applyChallengeResponseToGameState(
          state,
          action.payload as Record<string, unknown>
        );
      })
      .addCase(submitSkipChallenge.rejected, (state, action) => {
        state.loading = false;
        // A recoverable two-device race is shown as a toast, not an error banner.
        if (!(action.meta as { turnErrorCode?: string | null }).turnErrorCode) {
          state.error = action.payload as string;
        }
      });
  },
});

export const {
  clearError,
  setCurrentPrompt,
  clearCurrentPrompt,
  applyGameSettingsLocal,
  initializeGame,
  switchPlayer,
  updatePlayerScore,
  skipChallengeTurn,
  resetGame,
  setCurrentSession,
  applyRemoteSession,
  applyRemoteAction,
  setTurnError,
  clearTurnError,
} = gameSlice.actions;
export default gameSlice.reducer;
